/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormikProvider, useFormik, Form } from 'formik';
import { Layout, AutoComplete } from 'antd';
import { useStore } from '../../../store';
import { USER_ID } from '../../../store/modules/user/user.constants';
import { createValidator } from '../../../utils/create-validator';
import { CreateUserRequestDTO } from '../../../store/modules/user/dto/create-user.dto';
import FormControl from '../../lib/form-control';
import InputSelect from '../../lib/input-select';
import PageTitle from '../../lib/page-title';
import { useActions } from '../../providers/actions-provider/actions.context';
import { USER_MANAGEMENT_PATH } from '../../routing/paths.constants';
import PrimaryButton from '../../lib/primary-button';
import { useLocalStorage } from '../../providers/local-storage-provider/local-storage.context';
import './user-creation.less';

const UserDetails = (): JSX.Element => {
  const {
    roles, groups, currentUser, facilities, timeZones,
  } = useStore();
  const { userActions, tenantActions, facilityActions } = useActions();
  const { push } = useHistory();
  const localStorageService = useLocalStorage();
  const userID: number | null = localStorageService.get(USER_ID);
  const [roleCheck, setRoleCheck] = useState('');
  const [timeZonesList, setTimeZonesList] = useState<{ value: string, label: string }[]>([]);

  useEffect(() => {
    if (userID) {
      userActions.getCurrentUser(userID);
    }
    userActions.getRolesList();
    userActions.getTimeZones();
    tenantActions.getTenantsList();
    facilityActions.getGroups();
    facilityActions.getFacilities();
  }, [userID]);

  useEffect(() => {
    setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
  }, [timeZones]);

  const groupsList = Object.values(groups.data);
  const formik = useFormik({
    initialValues: {
      name: '',
      firstname: '',
      lastname: '',
      email: '',
      tenant: currentUser.data.tenant,
      role_id: 0,
      password: '',
      group_code: null,
      timeZone: '',
      facility: '',
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (roleCheck !== 'none') {
        await userActions.createUser(values);
        await userActions.getUsersList();
        push(USER_MANAGEMENT_PATH);
      }
    },
    validate: (values: CreateUserRequestDTO) => createValidator(CreateUserRequestDTO, values),
  });
  const { setFieldValue, values } = formik;

  const onTimeZoneSearch = (searchText: string) => {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText) {
      const opts = (timeZones.data || []).filter(
        (timeZone) => (
          timeZone.toLowerCase().includes(trimmedSearchText.toLowerCase())),
      );
      setTimeZonesList(opts.map((t) => ({ value: t, label: t })));
    } else {
      setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
    }
  };

  function filterRolesByLoggedInUser() {
    let dataRoles = roles.data;
    const role = roles.data.find((r) => r.id === currentUser.data.role_id);
    switch (role?.code) {
      case 'tenant_root':
        dataRoles = roles.data.filter((r) => r.code !== 'root');
        break;
      case 'tenant_admin':
        dataRoles = roles.data.filter((r) => r.code !== 'root' && r.code !== 'tenant_root');
        break;
      case 'tenant_user':
        dataRoles = roles.data.filter((r) => r.code !== 'root' && r.code !== 'tenant_root' && r.code !== 'tenant_admin');
        break;
      default:
        break;
    }
    return dataRoles;
  }

  return (
    <Layout className="main-container user-details">
      <PageTitle title="New User" back />
      {currentUser.data.tenant && (
        <FormikProvider value={formik}>
          <Form className="formik-form">
            <FormControl inputClassName="formik-form__input" name="name" label="Username" type="text" />
            <FormControl inputClassName="formik-form__input" name="firstname" label="First Name" type="text" />
            <FormControl inputClassName="formik-form__input" name="lastname" label="Last Name" type="text" />
            <FormControl inputClassName="formik-form__input" name="email" label="Email" type="text" />
            <div className="formik-form__separator" />
            <FormControl inputClassName="formik-form__input" name="password" label="Password" type="password" />
            <div className="formik-form__separator" />
            <div className="formik-form__wrapper user-form__wrapper">
              <InputSelect
                title="Role"
                defaultValue=""
                fieldName="role_id"
                onChange={(value: string | number) => {
                  setFieldValue('role_id', value);
                  setRoleCheck('');
                  document.querySelector('.ant-select-selector')?.classList.remove('required');
                }}
                data={filterRolesByLoggedInUser()}
              />
              {roleCheck === 'none' && (
                <p style={{ color: '#E13333', marginTop: -20 }} className="form-control__error">Role must be chosen</p>
              )}
              {/* <InputSelect
                title="Tenant"
                defaultValue={currentUser.data.tenant}
                disabled
                fieldName="tenant"
                onChange={(value: string | number) => {
                  setFieldValue('tenant', value);
                }}
                data={tenants.data}
              /> */}
              <InputSelect
                title="Facility"
                defaultValue=""
                fieldName="group_code"
                onChange={(value: string | number) => {
                // eslint-disable-next-line array-callback-return
                  groupsList.find((el) => {
                    if (el.name === value) {
                      setFieldValue('group_code', el.code);
                    }
                  });
                }}
                data={facilities.data}
                allowEmpty
              />
              <>
                <p className="ant-col ant-form-item-label" style={{ margin: '10px 0 5px' }}>Time Zone</p>
                <AutoComplete
                  options={timeZonesList}
                  value={values.timeZone}
                  style={{ width: '100%' }}
                  onSearch={onTimeZoneSearch}
                  onChange={(value:string) => {
                    setFieldValue('timeZone', value);
                  }}
                  onFocus={() => {
                    setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
                  }}
                />
              </>

            </div>
            <PrimaryButton
              type="submit"
              className="formik-form__submit"
              onClick={() => {
                if (values.role_id === 0) {
                  setRoleCheck('none');
                  document.querySelector('.ant-select-selector')?.classList.add('required');
                }
              }}
            >
              Save
            </PrimaryButton>
          </Form>
        </FormikProvider>
      )}
    </Layout>
  );
};

UserDetails.displayName = 'UserDetails';

export default UserDetails;
